import React from 'react';
import {
  BulkUploadDialogRoot,
  BulkUploadFullPageRoot,
} from '../styles';
import { getBulkUploadVariant, isBulkUploadPlanLocked } from '../rules';
import { BulkUploadUpgradePopup } from './BulkUploadPlanGate';
import { useProfile } from '../../state/hooks';
import { useDraftPageResume } from '../../../hooks/useDraftPageResume';
import { PendingBatchRecoveryState } from './PendingBatchRecoveryState';
import {
  SchoolTeacherBulkUploadFlow,
  IndependentTeacherBulkUploadFlow,
  TasksBulkUploadFlow,
} from './BulkUploadFlow';
import BulkUploadPendingStep, { TASKS_BULK_UPLOAD_STEPS } from './BulkUploadPendingStep';

const FLOW_BY_VARIANT = {
  schoolTeacher: SchoolTeacherBulkUploadFlow,
  independentTeacher: IndependentTeacherBulkUploadFlow,
};

/**
 * The Tasks wizard, held back until its draft-page snapshot is known.
 *
 * Mounting on an empty page list is what makes this worth waiting for: the
 * wizard autosaves, so a flow that opened empty would write that emptiness over
 * the stored snapshot before the resume ever landed.
 *
 * An unreadable snapshot is not an empty one either. It runs unpersisted rather
 * than blocking — the assignment is published and the teacher came here to add
 * responses — which is exactly the behaviour that shipped before, unsaved-work
 * guard and all.
 *
 * Keep the snapshot's version when resuming so uploads and routing survive a
 * refresh. An unreadable snapshot must never be overwritten by an empty one.
 */
const ResumedTasksFlow = ({ assignmentId, ...props }) => {
  const { resume, isLoading, isUnreadable, isBatchSaving, isRecoveryError, refetch } = useDraftPageResume(assignmentId);

  if (isRecoveryError) return <PendingBatchRecoveryState isError onRetry={() => refetch()} />;
  if (isBatchSaving) return <PendingBatchRecoveryState />;
  // The resume read can take seconds on a large class; an empty window read as
  // broken (JED-1556 follow-up).
  if (isLoading) return (
    <BulkUploadPendingStep steps={TASKS_BULK_UPLOAD_STEPS} currentStep={1}
      onBack={props.onExit} onClose={props.onExit} fullPage={props.fullPage} />
  );

  return (
    <TasksBulkUploadFlow
      assignmentId={assignmentId}
      resume={resume ?? undefined}
      persistDraftPages={!isUnreadable}
      {...props}
    />
  );
};

/**
 * Which frame the wizard renders in, named by the caller.
 *
 * `dialog` fills the paper BulkUploadDialog already owns; `page` is the fixed
 * overlay a route destination needs. There is deliberately no default: the prop
 * used to be an `inDialog` boolean defaulting to false, so a call site that
 * forgot it rendered an overlay that escaped its dialog — which is how the
 * "Add question" step came back as a screen covering /drafts/:assignmentId.
 */
const ROOT_BY_FRAME = {
  dialog: BulkUploadDialogRoot,
  page: BulkUploadFullPageRoot,
};

export const BulkUploadFullPage = ({
  onExit,
  onComplete,
  onDraftCreated,
  entry = 'drafts',
  assignmentId,
  submission,
  students,
  frame,
}) => {
  const { data: profile } = useProfile();
  const variant = getBulkUploadVariant(profile);
  const Root = ROOT_BY_FRAME[frame];

  // Before the variant check: an unframed call site is a mistake to surface
  // wherever it is made, not only for the profiles that reach the wizard.
  if (!Root) throw new Error(`BulkUploadFullPage needs frame="dialog" or "page", got ${frame}`);

  // Every entry point guards its click, but a call site that reaches here on the
  // free tier (a stale profile, a deep link) must not open an empty dialog: show
  // the upgrade prompt and let closing it close the frame.
  if (!variant && isBulkUploadPlanLocked(profile)) {
    return <BulkUploadUpgradePopup isOpen onClose={onExit} profile={profile} />;
  }

  if (!variant) return null;

  // Use TasksBulkUploadFlow (skip question) if:
  // 1. entry === 'tasks' OR
  // 2. assignmentId exists (adding to existing assignment)
  const finalAssignmentId = assignmentId || submission?.assignmentId;
  const shouldSkipQuestion = entry === 'tasks' || !!finalAssignmentId;

  if (shouldSkipQuestion) {
    const handleTasksComplete = (response) => onComplete?.(response);

    return (
      <Root $transparent={entry === 'drafts'}>
        <ResumedTasksFlow
          embedded
          fullPage
          assignmentId={finalAssignmentId}
          submission={submission}
          students={students}
          onExit={onExit}
          onComplete={handleTasksComplete}
        />
      </Root>
    );
  }

  const Flow = FLOW_BY_VARIANT[variant] || SchoolTeacherBulkUploadFlow;

  // For My Drafts, show DONE screen and let user exit manually
  // For other entries, use fullPage mode to navigate immediately
  const useFullPageMode = entry !== 'drafts';

  return (
    <Root $transparent={entry === 'drafts'}>
      <Flow
        embedded
        fullPage={useFullPageMode}
        showSettingsFirst
        assignmentId={assignmentId || submission?.assignmentId}
        submission={submission}
        students={students}
        onExit={onExit}
        onComplete={onComplete}
        onDraftCreated={onDraftCreated}
      />
    </Root>
  );
};
